<template>

  <div id="wizardContainer" class="container mt-5">

    <form-wizard title="mDrive Signup Wizard" subtitle="Fast Track through signing up with mDrive"
                 @on-error="handleErrorMessage"
                 @on-complete="wizardFinished"
                 color="#0060a8">
      <div slot="title">
        <h3 class="wizard-title">mDrive Signup Wizard</h3>
        <h3 class="wizard-subtitle">Fast Track through signing up with mDrive</h3>
      </div>
      <tab-content title="Defaults" class="wizard-tab" :before-change="validateDefaultsAsync">

        <b-alert class="wizard-helper-text" show variant="info" dismissible>
          <h4 class="alert-heading">
            mDrive Default Scan Settings
          </h4>
          <hr />
          Configure your default mDrive scan settings here. Click Save when done.
        </b-alert>
        <b-alert class="wizard-helper-text" show variant="info" dismissible>
          <h4 class="alert-heading">
            Welcome to mDrive Sync!
          </h4>
          <hr />
          <p>Please take a moment to walk through this wizard and configure your mDrive account. If at any point during the wizard you need help, click on the headset icon to contact Marketscan support.</p>
          <p>We thank you for your business and hope you enjoy mDrive and the mDrive Sync app!</p>
          <hr />
          <strong>Hint: You can dismiss these alerts by clicking the x in the upper right corner.</strong>
        </b-alert>
        <Defaults forceSyncScreenHide="true" @clicked="defaultsSaved=true"></Defaults>
      </tab-content>
      <tab-content title="Users" class="wizard-tab">
        <Users></Users>
      </tab-content>
      <tab-content title="Lenders" class="wizard-tab" :before-change="validateLendersAsync">
        <b-alert class="wizard-helper-text" show variant="info" dismissible>
          <h4 class="alert-heading">
            Manage Tracked Lenders
          </h4>
          <hr />
          Enable lenders from the Available Lenders column (left side) from below to add them to your list of tracked lenders, then click Save when finished.
        </b-alert>
        <Lenders ref="lenders" @clicked="savingLenders"></Lenders>
      </tab-content>
      <tab-content title="Dealer Fees" class="wizard-tab" :before-change="validateFeesAsync">
        <b-alert class="wizard-helper-text" show variant="info" dismissible>
          <h4 class="alert-heading">
            Manage Custom Taxes and Fees
          </h4>
          <hr />
          Please add at least one fee to a maximum of 10 fees.
        </b-alert>
        <DealerFees @clicked="feesSaved=true"></DealerFees>
      </tab-content>
      <tab-content title="Inventory Rules" class="wizard-tab">
        <b-alert class="wizard-helper-text" show variant="info" dismissible>
          <h4 class="alert-heading">
            Manage Inventory Quoting Rules
          </h4>
          <hr />
          Please add at least 1 quoting rule.  Note that policies automatically apply appropriate rebates.
        </b-alert>
        <InventoryQuoting hideAllPartners="true"></InventoryQuoting>
      </tab-content>
      <tab-content title="Market Settings" class="wizard-tab" :before-change="validateMarketSettingsAsync">
        <MarketSettings @clicked="salesTaxSaved=true"></MarketSettings>
      </tab-content>
      <tab-content title="Calculation Rules" class="wizard-tab" :before-change="validateCalcSettingsAsync">
        <CalculationSettings @clicked="calcSettingsSaved=true"></CalculationSettings>
      </tab-content>
      <tab-content title="Inventory" class="wizard-tab">
        <div class="card">
          <div class="card-header">
            <strong class="mdrive-header-text">Last step - please complete the docu-sign</strong>
          </div>
          <iframe class="embed-responsive-item docusignContainer"
                  src="https://na3.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=a2da06e6-200e-4209-b53e-8ca93a7ee4f6&env=na3&acct=d0c21fd3-61d0-43db-8468-26c3edc497ce&v=2"></iframe>
        </div>
          </tab-content>
      <tab-content title="Complete" class="wizard-tab">

        <h3 class="text-info text-center">
          Thank you for configuring your account!
        </h3>
        <p>
          To configure your inventory and get finished setting up mDrive Sync, please contact our support.
          When you click finish, you will be redirected to mDrive web. To download mDrive Mobile, please
          click on the appropriate app store link below.
        </p>

        <div style='display:flex;justify-content:space-around'>
          <a href='https://apps.apple.com/us/app/mdrive-sync/id1492364834'>
            <img src="@/assets/apple-app-store-icon.png" alttext='Download mDrive Sync for Apple' />
          </a>
          <a href='https://play.google.com/store/apps/details?id=com.marketscan.mdrive'>
            <img src="@/assets/android-app-store-icon.png" alttext='Download mDrive Sync for Android' />
          </a>
        </div>

      </tab-content>
    </form-wizard>
    <div class="wizard-thumbprint-bg"></div>
    <b-alert variant="danger" :show="showErrorMessage()" dismissible ref="errAlert">
      {{errorMsg}}
    </b-alert>
    <a class="btn btn-link " href="https://www.marketscan.com/support" title="Get Online Support" target="_blank">
      <div class="support-circle">
        <div class="sticky-headset">
          <font-awesome-icon icon="headset" size="4x" />
        </div>
        <div class="support-circle-text">Click here to <br />get support</div>
      </div>
    </a>
  </div>
</template>

<script>
import Vue from 'vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

import DealerInfo from '@/views/DealerInformation'
import Defaults from '@/views/DealerSettings'
import Users from '@/views/UserManagement'
import Lenders from '@/views/Lenders'
import DealerFees from '@/views/DealerDefinedFees'
import InventoryQuoting from '@/views/QuotePolicies'
import MarketSettings from '@/views/MarketSpecificSettings'
import CalculationSettings from '@/views/CalculationSettings'
import Inventory from '@/views/Inventory'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faHeadset } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
library.add(faHeadset)
Vue.component('font-awesome-icon', FontAwesomeIcon)

export default {
  name: 'wizard',
  data () {
    return {
      defaultsSaved: false,
      lendersSaved: false,
      feesSaved: false,
      salesTaxSaved: false,
      calcSettingsSaved: false,
      errorMsg: ''
    }
  },
  components: {
    FormWizard,
    TabContent,
    DealerInfo,
    Defaults,
    Users,
    Lenders,
    DealerFees,
    InventoryQuoting,
    MarketSettings,
    CalculationSettings,
    Inventory
  },
  methods: {
    validateDefaultsAsync: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!this.defaultsSaved) {
            reject('Please save your defaults to continue.')
          } else {
            resolve(true)
          }
        }, 1000)
      })
    },
    validateLendersAsync: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (this.lendersSaved === 'false') {
            reject('Please save your active lenders to continue. You must select at least one.')
          } else {
            resolve(true)
          }
        }, 1000)
      })
    },
    validateFeesAsync: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!this.feesSaved) {
            reject('Please save your fees to continue. You must have at least one fee.')
          } else {
            resolve(true)
          }
        }, 1000)
      })
    },
    validateMarketSettingsAsync: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!this.salesTaxSaved) {
            reject('Please configure taxes and fees for at least one market before continuing.')
          } else {
            resolve(true)
          }
        }, 1000)
      })
    },
    validateCalcSettingsAsync: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!this.calcSettingsSaved) {
            reject('Please save your calculation settings before continuing.')
          } else {
            resolve(true)
          }
        }, 1000)
      })
    },
    savingLenders: function (validCount) {
      this.lendersSaved = validCount
      this.$refs.errAlert.show = false
    },
    handleErrorMessage: function (errorMsg) {
      this.errorMsg = errorMsg
    },
    showErrorMessage: function () {
      if (this.errorMsg == undefined || this.errorMsg == null || this.errorMsg.length <= 1) {
        return false
      }
      return true
    },
    wizardFinished: function () {
      this.$router.push('/mDrive')
    }
  }
}
</script>

<style scoped>
  .wizard {
    z-index: 500;
  }
  .wizard-tab{
    padding: 10px;
    min-height:58vh;
    max-height:58vh;
    overflow:auto;

  }
  .wizard-thumbprint-bg {
    width: calc(100% - 6rem);
    height: calc(100% - 6rem);
    background-image: url(https://www.marketscan.com/Portals/0/blue-thumbprint-5.png);
    background-size: auto 100%;
    background-repeat: no-repeat;
    background-position: center;
    position: absolute;
    margin: 3rem;
    top: 0;
    left: 0;
    z-index: -1;
  }
  .wizard-helper-text{
    position:fixed;
    top:200px;
    left:25vw;
    width:50vw;
    z-index:1000;
    text-align:center;
  }
  .wizard-title,
  .wizard-subtitle {
    color: #231f20 !important;
    font-family: Calibri !important;
  }
  .wizard-title {
    font-size: 2rem !important;
    font-weight: bold !important;
  }
  .wizard-subtitle {
    font-size: 1.75rem !important;
    font-weight: 100 !important;
  }
  .docusignContainer{
      height:55vh;
      width:100%;
  }
  span.error {
    color: #e74c3c;
    font-size: 20px;
    display: block;
    text-align: center;
  }

  .support-circle {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: #e4e5e7;
    position: fixed;
    right: 25px;
    bottom: 125px;
    z-index: 1030;
  }

  .sticky-headset > svg {
    width: 60px;
    height: auto;
    position: absolute;
    left: calc(50% - 30px);
    top: calc(50% - 30px);
    z-index: 1030;
  }

  .support-circle-text {
    width: 100%;
    height: 1em;
    text-align: center;
    line-height: 1.25em;
    color: #0060a8;
    position: absolute;
    bottom: -1.75em;
    left: 0;
  }

  .fa-headset {
    color: #0060a8;
  }

  /* Step 1 */

  .container,
  .wizard-tab-container {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .wizard-helper-text {
    padding-top: 30px;
    padding-bottom: 30px;
  }

  /* Card Header */
  .card-header {
    padding: 1.25rem calc(1.25rem + 15px) !important;
  }

  #btnSave {
    /* border-radius: .25rem !important; */
  }
  @media (max-width: 920px) {
    .wizard-thumbprint-bg {
      background-size: 100% auto;
      background-position: top center;
    }
    .support-circle {
      width: 80px;
      height: 80px;
      bottom: 25px;
      right: initial;
      left: 25px;
    }

    .sticky-headset > svg {
      width: 40px;
      left: calc(50% - 20px);
      top: calc(50% - 20px);
    }
    .support-circle-text {
      display: none;
    }
    .vue-form-wizard {
      padding-bottom: 100px;
    }
  }

</style>



// WEBPACK FOOTER //
// src/views/mDrive/mDriveWizard.vue